import React from 'react'

import * as c from '../../../common'

export interface PageReportProps {
  /**
   * This allows the page to grow tall as needed to accommodate for content, but
   * no longer resembling a real sheet of paper's dimensions, use for pages that
   * are just virtual and won't be rendered into the PDF for printing.
   */
  autoHeight?: boolean
  children: React.ReactNode
  company: string
  companyBorder?: boolean
  /**
   * Enables some horizontal padding on the page.
   */
  hPad?: boolean
  /**
   * Excludes this page from the generated PDF.
   */
  noPDF?: boolean
  title?: string
  vPadDisable?: boolean
}

export default React.memo<PageReportProps>(function PageReport({
  autoHeight,
  children,
  company,
  companyBorder,
  hPad,
  noPDF,
  title,
  vPadDisable,
}) {
  const [padCompanyStyleTop, padCompanyStyleBottom] = React.useMemo((): [
    React.CSSProperties,
    React.CSSProperties,
  ] => {
    const base = {
      backgroundColor: c.companyColors[company],
      position: 'absolute' as const,
      left: 0,
      height: `${borderHeight}px`,
      width: '100%',
    }

    return [
      { ...base, top: 0 },
      { ...base, bottom: 0 },
    ]
  }, [company])
  const pageStyle = React.useMemo(
    (): React.CSSProperties => ({
      alignItems: 'center',
      backgroundColor: '#FFF',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      // marginBottom: '32px',
      maxHeight: autoHeight ? undefined : `${pageHeight}px`,
      minHeight: `${pageHeight}px`,
      paddingBottom: vPadDisable
        ? 0
        : companyBorder
        ? `${verticalPad + borderHeight}px`
        : `${verticalPad}px`,
      paddingLeft: hPad ? `${horizontalPad}px` : undefined,
      paddingRight: hPad ? `${horizontalPad}px` : undefined,
      paddingTop: vPadDisable
        ? 0
        : companyBorder
        ? `${verticalPad + borderHeight}px`
        : `${verticalPad}px`,
      position: 'relative',
      transition: 'all ease 1s',
      width: `${pageWidth}px`,
    }),
    [autoHeight, companyBorder, hPad, vPadDisable],
  )

  return (
    <div data-pdf-page={noPDF ? undefined : ''} style={pageStyle}>
      {companyBorder && <div style={padCompanyStyleTop}></div>}

      {title && <h1 style={titleStyle}>{title}</h1>}

      {children}

      {companyBorder && <div style={padCompanyStyleBottom}></div>}
    </div>
  )
})

export const borderHeight = 24
export const horizontalPad = 80
export const pageWidth = 800
// A4 aspect ratio
export const pageHeight = 1128
export const verticalPad = 32

export const titleHeight = 42

const titleStyle = {
  color: '#000000',
  fontFamily: c.light.fontFamily,
  fontSize: '28px',
  // fontSize: 28,
  marginBottom: 0,
  height: titleHeight,
}
